package com.bikompanion.ui.home

import dev.zacsweers.metro.Assisted
import dev.zacsweers.metro.AssistedFactory
import dev.zacsweers.metro.AssistedInject
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import com.bikompanion.domain.BicingNetworkId
import com.bikompanion.domain.BikeIssue
import com.bikompanion.domain.BikeKey
import com.bikompanion.domain.BikeNetwork
import com.bikompanion.domain.BikeRatingRepository
import com.bikompanion.domain.BikeScopeSource
import com.bikompanion.domain.ElectricOnlyIssues
import com.bikompanion.domain.RatingPreferencesRepository
import com.bikompanion.domain.SubmitVoteResult
import com.bikompanion.domain.catchingRequest
import com.bikompanion.domain.usecase.ResolveNetworkScopeUseCase
import com.bikompanion.domain.usecase.SubmitBikeVoteUseCase
import com.bikompanion.ui.mapper.toSummary
import com.bikompanion.ui.models.BikeRatingMessage
import com.bikompanion.ui.models.BikeRatingUiState
import kotlin.time.Duration
import kotlin.time.Duration.Companion.milliseconds

/** How long typing has to pause before the entered bike number is looked up. */
private val ScoreLookupDebounce = 300.milliseconds

/** Everything behind the rate-a-bike sheet. */
@AssistedInject
class BikeRatingHandler(
    @Assisted private val session: HomeSession,
    private val ratingRepository: BikeRatingRepository,
    private val ratingPreferences: RatingPreferencesRepository,
    private val resolveNetworkScope: ResolveNetworkScopeUseCase,
    private val submitBikeVote: SubmitBikeVoteUseCase,
    @Assisted private val parkingCountdown: ParkingCountdown,
) {
    @AssistedFactory
    fun interface Factory {
        fun create(
            session: HomeSession,
            parkingCountdown: ParkingCountdown,
        ): BikeRatingHandler
    }

    private val state get() = session.state
    private var scoreLookupJob: Job? = null

    /**
     * Takes the typed number and looks up how that bike has been rated so far.
     *
     * Digits only: the numbers are printed on the bikes as plain integers, and letting anything
     * else through would split one bike's votes across several keys that look the same.
     *
     * The lookup is debounced rather than fired per keystroke, so typing a four-digit number
     * costs one read instead of four — and the last one to start is the only one allowed to
     * publish, so a slow earlier lookup can't overwrite the current number's score.
     */
    fun onBikeNumberChanged(bikeNumber: String) {
        val digits = bikeNumber.filter { it.isDigit() }
        if (digits == state.value.ratingUiState.bikeNumber) return
        scoreLookupJob?.cancel()
        val networkId = state.value.ratingUiState.selectedNetworkId
        // A number with no network settled is not a bike yet, so there is nothing to look up —
        // the rider is asked to pick one first (see BikeRatingUiState.needsNetworkChoice), and
        // choosing runs the lookup from onNetworkSelected.
        val key = if (digits.isEmpty() || networkId == null) null else BikeKey(networkId, digits)
        updateRating {
            it.copy(
                bikeNumber = digits,
                statusMessage = null,
                // The old bike's score has nothing to say about this one.
                score = null,
                scoreSummary = null,
                // Only ever true while a lookup is genuinely running. Set from `digits` alone it
                // would stay on forever in the no-network case below, leaving the field spinning
                // at a rider whose number was never going anywhere.
                isCheckingScore = key != null,
            )
        }
        scoreLookupJob = key?.let { startScoreLookup(it, debounce = ScoreLookupDebounce) }
    }

    /**
     * Looks [key] up and publishes it, clearing the spinner whichever way it goes.
     *
     * Every caller cancels the job before it, so the last lookup to start is the only one that
     * can publish — a slow earlier one can't overwrite the current number's score.
     */
    private fun startScoreLookup(
        key: BikeKey,
        debounce: Duration,
    ): Job =
        session.scope.launch {
            if (debounce > Duration.ZERO) delay(debounce)
            val score = catchingRequest { ratingRepository.getScore(key) }.getOrNull()
            updateRating {
                it.copy(
                    score = score,
                    scoreSummary = score?.toSummary(),
                    isCheckingScore = false,
                )
            }
        }

    fun onElectricToggled(isElectric: Boolean) {
        updateRating {
            it.copy(
                isElectric = isElectric,
                // Electric-only issues stop applying the moment the toggle turns off.
                selectedIssues = if (isElectric) it.selectedIssues else it.selectedIssues - ElectricOnlyIssues,
            )
        }
    }

    /** Only records which side the user picked; [onSubmitClick] is what actually sends it. */
    fun onVoteSelected(isPositive: Boolean) {
        updateRating {
            it.copy(
                selectedVote = isPositive,
                statusMessage = null,
                // Electric-only issues can't apply once the vote flips back to good.
                selectedIssues = if (isPositive) emptySet() else it.selectedIssues,
            )
        }
    }

    fun onIssueToggled(issue: BikeIssue) {
        updateRating {
            val current = it.selectedIssues
            it.copy(selectedIssues = if (issue in current) current - issue else current + issue)
        }
    }

    fun onSubmitClick() {
        val ratingUiState = state.value.ratingUiState
        val bikeNumber = ratingUiState.bikeNumber.trim()
        val vote = ratingUiState.selectedVote
        val networkId = ratingUiState.selectedNetworkId
        if (bikeNumber.isEmpty() || vote == null || ratingUiState.isSubmitting) return
        if (networkId == null) {
            updateRating { it.copy(statusMessage = BikeRatingMessage.NetworkRequired) }
            return
        }
        submitVote(
            key = BikeKey(networkId, bikeNumber),
            isPositive = vote,
            issues = if (vote) emptyList() else ratingUiState.selectedIssues.toList(),
        )
    }

    /**
     * Tells the model where the rider is, so it can work out which network a bike number belongs to.
     *
     * Called with whatever the screen can offer, best first: a real position fix, or failing that
     * where the map is pointed — if a rider panned somewhere, they have implicitly said where they
     * are, and the map always has a position where GPS may not.
     *
     * A single candidate is selected outright (the rider can still change it); two or more are
     * left for them to choose between — unless the last one they voted under is among them, in
     * which case that is the better guess: the rider standing between two systems most likely
     * still has the same one in hand.
     */
    fun onPositionChanged(
        latitude: Double,
        longitude: Double,
        source: BikeScopeSource,
    ) {
        val candidates = resolveNetworkScope(state.value.networks, latitude, longitude)
        updateRating { rating ->
            val stillValid = rating.selectedNetworkId?.takeIf { id -> candidates.any { it.network.id == id } }
            val remembered = ratingPreferences.getLastNetworkId()?.takeIf { id -> candidates.any { it.network.id == id } }
            rating.copy(
                networkCandidates = candidates,
                selectedNetworkId = stillValid ?: remembered ?: candidates.singleOrNull()?.network?.id,
                scopeSource = if (stillValid != null) rating.scopeSource else source,
            )
        }
    }

    /** The rider naming the network themselves, which overrides whatever position resolved to. */
    fun onNetworkSelected(network: BikeNetwork) {
        ratingPreferences.setLastNetworkId(network.id)
        updateRating {
            it.copy(
                selectedNetworkId = network.id,
                scopeSource = BikeScopeSource.ManualPick,
                statusMessage = null,
                // A number means something different in a different network, so nothing carries over.
                score = null,
                scoreSummary = null,
            )
        }
        // Re-look-up under the network just chosen; the number itself has not changed, so there
        // is nothing to debounce — this is a tap, not typing.
        state.value.ratingUiState.bikeNumber
            .takeIf { it.isNotEmpty() }
            ?.let { number ->
                scoreLookupJob?.cancel()
                updateRating { it.copy(isCheckingScore = true) }
                scoreLookupJob = startScoreLookup(BikeKey(network.id, number), debounce = Duration.ZERO)
            }
    }

    /** Clears the toast once the screen has shown it, so it doesn't replay on recomposition. */
    fun onToastShown() {
        updateRating { it.copy(toastMessage = null) }
    }

    private fun submitVote(
        key: BikeKey,
        isPositive: Boolean,
        issues: List<BikeIssue>,
    ) {
        val ratingUiState = state.value.ratingUiState
        val isElectric = ratingUiState.isElectric
        val scopeSource = ratingUiState.scopeSource
        updateRating { it.copy(isSubmitting = true, statusMessage = null) }
        session.scope.launch {
            when (val result = submitBikeVote(key, isPositive, isElectric, issues, scopeSource)) {
                is SubmitVoteResult.Recorded -> {
                    val score = catchingRequest { ratingRepository.getScore(key) }.getOrNull()
                    updateRating {
                        it.copy(
                            isSubmitting = false,
                            selectedVote = null,
                            selectedIssues = emptySet(),
                            score = score,
                            scoreSummary = score?.toSummary(),
                            toastMessage = BikeRatingMessage.Thanks(key.bikeNumber),
                        )
                    }
                    if (key.networkId == BicingNetworkId) parkingCountdown.start()
                }

                is SubmitVoteResult.InCooldown -> {
                    updateRating {
                        it.copy(
                            isSubmitting = false,
                            selectedVote = null,
                            selectedIssues = emptySet(),
                            // Toasted rather than shown in the sheet: there is nothing left to do
                            // in there until the cooldown is over, so it closes like a success does.
                            toastMessage = BikeRatingMessage.InCooldown(result.retryAfterMs),
                        )
                    }
                }

                is SubmitVoteResult.Queued -> {
                    updateRating {
                        it.copy(
                            isSubmitting = false,
                            selectedVote = null,
                            selectedIssues = emptySet(),
                            statusMessage = BikeRatingMessage.Queued,
                        )
                    }
                }

                is SubmitVoteResult.Failed -> {
                    updateRating {
                        it.copy(
                            isSubmitting = false,
                            statusMessage = BikeRatingMessage.Failed(result.reason),
                        )
                    }
                }
            }
        }
    }

    fun onDialogDismissed() {
        scoreLookupJob?.cancel()
        scoreLookupJob = null
        state.update { it.copy(ratingUiState = BikeRatingUiState()) }
    }

    private inline fun updateRating(crossinline block: (BikeRatingUiState) -> BikeRatingUiState) {
        state.update { it.copy(ratingUiState = block(it.ratingUiState)) }
    }
}
